const TZ = "Europe/Berlin";

export function fmtDate(v?: string | null): string {
  if (!v) return "–";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split("-");
    return `${d}.${m}.${y}`;
  }
  return new Intl.DateTimeFormat("de-DE", { timeZone: TZ, day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(v));
}

export function fmtDateTime(v?: string | null): string {
  if (!v) return "–";
  const d = new Date(v);
  const t = new Intl.DateTimeFormat("de-DE", { timeZone: TZ, hour: "2-digit", minute: "2-digit" }).format(d);
  return `${fmtDate(v)}, ${t} Uhr`;
}

export function fmtSize(b: number): string {
  if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
  return `${(b / 1024 ** 3).toFixed(2).replace(".", ",")} GB`;
}

/** Setzt Punkte automatisch (TT.MM.JJJJ); akzeptiert Ziffern, Punkt und Komma. */
export function maskDateDE(raw: string, prev = ""): string {
  const seg = ["", "", ""];
  const typing = raw.length >= prev.length;
  let i = 0;
  for (const ch of raw.replace(/,/g, ".")) {
    if (/\d/.test(ch)) {
      if (seg[i].length < (i === 2 ? 4 : 2)) seg[i] += ch;
      if (i < 2 && seg[i].length === 2) i++;
    } else if (ch === "." && i < 2 && seg[i].length > 0) {
      if (typing) seg[i] = seg[i].padStart(2, "0");
      i++;
    }
  }
  let out = seg[0] + (i >= 1 ? "." + seg[1] : "") + (i >= 2 ? "." + seg[2] : "");
  if (raw.length < prev.length && out.endsWith(".")) out = out.slice(0, -1);
  return out;
}

export function parseGermanDate(s: string): string | null {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const [dd, mm, yy] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(yy, mm - 1, dd);
  if (d.getFullYear() !== yy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export const STATUS: Record<string, string> = {
  EINGEGANGEN: "Eingegangen", ANGENOMMEN: "Angenommen", RUECKFRAGE: "Rückfrage", ABGELEHNT: "Abgelehnt",
  PATIENT_KONTAKTIERT: "Patient kontaktiert", TERMIN_VEREINBART: "Termin vereinbart", BEHANDELT: "Behandelt",
  BERICHT_VERSENDET: "Bericht versendet", ABGESCHLOSSEN: "Abgeschlossen",
};
export const GRUND: Record<string, string> = {
  WEISHEITSZAHN: "Weisheitszahnentfernung", ZAHNENTFERNUNG: "Zahnentfernung", IMPLANTAT: "Implantation", AUGMENTATION: "Augmentation/Sinuslift",
  WSR: "Wurzelspitzenresektion", ZYSTE: "Zystenentfernung", BEFUND: "Abklärung Befund/Schleimhaut",
  FREILEGUNG: "Freilegung", DVT: "DVT-Diagnostik", SONSTIGES: "Sonstiges",
};
export const VERSICHERUNG: Record<string, string> = { GKV: "Gesetzlich (GKV)", PKV: "Privat (PKV)", BEIHILFE: "Beihilfe", SELBSTZAHLER: "Selbstzahler" };
export const DRINGLICHKEIT: Record<string, string> = { NORMAL: "Normal", ZEITNAH: "Zeitnah", SCHMERZFALL: "Schmerzfall" };
export const WUNSCH: Record<string, string> = { NUR_DIAGNOSTIK: "Nur Diagnostik", NUR_CHIRURGIE: "Nur chirurgischer Eingriff", KOMPLETT: "Komplette Behandlung" };
export const DATEITYP: Record<string, string> = { OPG: "OPG", ZAHNFILM: "Zahnfilm", DVT: "DVT", FOTO: "Foto", UEBERWEISUNGSSCHEIN: "Überweisungsschein", SONSTIGES: "Sonstiges" };
export const ROLLE: Record<string, string> = { ADMIN: "Administrator", MITARBEITER: "Mitarbeiter" };

function hexToRgb(h: string) {
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
}
const toHex = (c: number[]) => "#" + c.map((x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, "0")).join("");

function lum(c: number[]) {
  const f = (x: number) => { x /= 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]);
}

export function brandVars(hex?: string): Record<string, string> {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return {};
  const rgb = hexToRgb(hex);
  let light = rgb;
  // Hellton so wählen, dass Text in Primärfarbe darauf mind. 4,5:1 erreicht
  for (const t of [0.9, 0.93, 0.96, 0.98, 1]) {
    light = rgb.map((c) => c + (255 - c) * t);
    if ((lum(light) + 0.05) / (lum(rgb) + 0.05) >= 4.5) break;
  }
  return {
    "--kd-primary": hex,
    "--kd-primary-dark": toHex(rgb.map((c) => c * 0.7)),
    "--kd-primary-light": toHex(light),
  } as any;
}
